import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');
  const provider = searchParams.get('provider') || 'stripe';
  const isMollie = provider === 'mollie';

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const checkPayment = async () => {
      try {
        const path = isMollie ? `/api/payments/mollie/status/${sessionId}` : `/api/payments/status/${sessionId}`;
        const response = await fetch(`${API_URL}${path}`);
        if (!response.ok) throw new Error('status ' + response.status);
        const data = await response.json();
        attempts += 1;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(data);
        } else if (['failed', 'canceled', 'expired'].includes(data.payment_status)) {
          setStatus('error');
        } else if (isMollie && (data.payment_status === 'pending' || attempts >= 5)) {
          setStatus('pending');
          setPaymentDetails(data);
        } else {
          setTimeout(checkPayment, 2000);
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setStatus('error');
      }
    };

    checkPayment();
  }, [sessionId, isMollie]);

  const isPending = status === 'pending';
  const formatAmount = (d) => `${(isMollie ? d.amount : d.amount / 100).toFixed(2)} ${(d.currency || 'EUR').toUpperCase()}`;

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-4">
        <Card className="bg-light/5 border-gold/20 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-16 h-16 text-gold animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-playfair text-white mb-2">
              Vérification du paiement...
            </h2>
            <p className="text-gray-400">
              Veuillez patienter quelques instants
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-4">
        <Card className="bg-light/5 border-red-500/20 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-playfair text-white mb-2">
              Erreur de vérification
            </h2>
            <p className="text-gray-400 mb-6">
              Impossible de vérifier le statut du paiement
            </p>
            <Button
              onClick={() => navigate('/services')}
              className="bg-gold hover:bg-gold-light text-dark"
            >
              Retour aux services
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-4">
      <Card className="bg-light/5 border-gold/20 max-w-2xl w-full">
        <CardHeader className="text-center border-b border-gold/20">
          <div className="w-20 h-20 bg-neon/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-neon" />
          </div>
          <CardTitle className="text-4xl font-playfair text-white" data-testid="payment-result-title">
            {isPending ? 'Paiement en cours' : paymentDetails?.type === 'subscription' ? 'Abonnement activé !' : 'Paiement Réussi !'}
          </CardTitle>
          <p className="text-gray-400 mt-2">
            {isPending ? 'Votre paiement est en cours de traitement par votre banque' : 'Merci pour votre confiance'}
          </p>
        </CardHeader>
        
        <CardContent className="pt-8">
          <div className="bg-white/5 rounded-sm p-6 border border-gold/20 mb-6">
            <h3 className="text-xl font-semibold text-white mb-4">Détails de la transaction</h3>
            <div className="space-y-3 text-gray-300">
              <div className="flex justify-between">
                <span>Référence :</span>
                <span className="text-gold font-mono text-sm">{sessionId}</span>
              </div>
              {paymentDetails?.amount && (
                <div className="flex justify-between">
                  <span>Montant :</span>
                  <span className="text-white font-semibold">
                    {formatAmount(paymentDetails)}{paymentDetails.type === 'subscription' ? ' / mois' : ''}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Statut :</span>
                <span className={`font-semibold ${isPending ? 'text-gold' : 'text-neon'}`} data-testid="payment-result-status">
                  {isPending ? 'En attente de confirmation bancaire' : 'Payé ✓'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gold/10 border border-gold/30 rounded-sm p-4 mb-6">
            <h4 className="text-gold font-semibold mb-2 flex items-center gap-2">
              <span>📧</span>
              Confirmation par email
            </h4>
            <p className="text-sm text-gray-300">
              Un email de confirmation a été envoyé avec tous les détails de votre commande.
              Notre équipe vous contactera sous 24h pour planifier la suite.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-gold hover:bg-gold-light text-dark"
            >
              <span>Retour à l'accueil</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/services')}
              variant="outline"
              className="w-full border-gold/30 text-gold hover:bg-gold/10"
            >
              Voir nos autres services
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};